// Datenpfad des Kiosk-Terminals — bewusst GETRENNT vom normalen api/client.ts.
//
// Gründe: (1) Das Terminal hat keine Session; es authentifiziert sich mit dem Geräte-Token im Header.
// (2) `credentials: 'omit'` — läge auf dem Tablet noch ein altes Session-Cookie, soll es hier keine Rolle
// spielen. (3) Der Impersonations-Header hat am Wandtablet nichts verloren.
// (4) Die Offline-Warteschlange ist ein EIGENER Bucket: die bestehende Outbox ist an `user.id` der Session
//     gebunden (offline/outbox.ts) und hat zwei bekannte Race-Fenster beim Identitätswechsel. Am Kiosk
//     wechselt die Identität im Sekundentakt — diese Fenster würden vom Randfall zum Normalfall. Der
//     erprobte Pfad bleibt deshalb unangetastet.

const TOKEN_KEY = 'wf.kiosk.token.v1'
const QUEUE_KEY = 'wf.kiosk.queue.v1'
const FAILED_KEY = 'wf.kiosk.queue.failed.v1'

/**
 * Uhren-Offset gegen die Server-Zeit (Muster: offline/serverClock.ts).
 *
 * Ohne ihn wäre jede Buchung von der Wanduhr des Tablets abhängig: der Server klassifiziert die Provenienz
 * aus `stamped_at` (>5 min Abweichung → Nachzügler, >72 h → Korrekturantrag). Ein Tablet, dessen Uhr 20
 * Minuten nachgeht, würde also jeden Live-Stempel in einen Nachtrag verwandeln.
 */
let serverOffsetMs = 0
export function noteKioskServerNow(serverNow: number): void {
  if (Number.isFinite(serverNow)) serverOffsetMs = serverNow - Date.now()
}
const serverAdjustedNow = (): number => Date.now() + serverOffsetMs

export interface KioskUser {
  id: number
  name: string
  color: string
}

export interface KioskStampResult {
  name: string
  device: string
  status: 'an' | 'pause' | 'aus'
  todaySeconds: number
  liveSince: number | null
  serverNow: number
  /** Tatsaechlich gebuchter Zeitpunkt — weicht bei Nachzueglern von serverNow ab. */
  bookedAt?: number
  converted?: 'correction'
  correctionId?: number
}

export type StampKind = 'kommen' | 'gehen' | 'pause_start' | 'pause_ende'

export function getKioskToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setKioskToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token)
  } catch {
    /* Storage gesperrt — dann bleibt das Terminal ungekoppelt, statt still Falsches zu tun. */
  }
}

export function clearKioskToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignorieren */
  }
}

export class KioskApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'KioskApiError'
  }
}

async function kioskRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getKioskToken()
  if (!token) throw new KioskApiError(401, 'Dieses Gerät ist nicht als Terminal eingerichtet')
  const res = await fetch(`/api/${path}`, {
    credentials: 'omit',
    headers: { 'content-type': 'application/json', 'X-Kiosk-Device': token, ...(init?.headers ?? {}) },
    ...init,
  })
  const text = await res.text()
  const body: unknown = text ? JSON.parse(text) : null
  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body ? String((body as { error: unknown }).error) : `Fehler ${res.status}`
    throw new KioskApiError(res.status, message)
  }
  return body as T
}

export function fetchRoster(): Promise<{ device: string; users: KioskUser[] }> {
  return kioskRequest('kiosk/roster')
}

// ---- Offline-Warteschlange ----

interface QueueItem {
  clientUuid: string
  userId: number
  kind: StampKind
  stampedAt: number
}

function loadQueue(): QueueItem[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY)
    return raw ? (JSON.parse(raw) as QueueItem[]) : []
  } catch {
    return []
  }
}

function saveQueue(items: QueueItem[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(items))
  } catch {
    /* ignorieren */
  }
}

export function queueSize(): number {
  return loadQueue().length
}

/** Nicht zustellbare Vorgänge — NIE stillschweigend gelöscht, sondern sichtbar aufbewahrt. */
export function failedQueueSize(): number {
  try {
    const raw = localStorage.getItem(FAILED_KEY)
    return raw ? (JSON.parse(raw) as QueueItem[]).length : 0
  } catch {
    return 0
  }
}

function deadLetter(item: QueueItem): void {
  try {
    const raw = localStorage.getItem(FAILED_KEY)
    const failed = raw ? (JSON.parse(raw) as QueueItem[]) : []
    localStorage.setItem(FAILED_KEY, JSON.stringify([...failed, item]))
  } catch {
    /* ignorieren */
  }
}

const newUuid = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `k-${Date.now()}-${Math.random().toString(16).slice(2)}`

/**
 * Stempelt am Terminal. Online mit PIN-Prüfung; ohne Verbindung wandert der Vorgang in die Warteschlange und
 * wird später als `unverified` nachgereicht — der Server macht daraus zwingend einen Korrektur-Antrag, nie
 * einen direkten Stempel (die PIN war ja nicht prüfbar).
 */
export async function kioskStamp(userId: number, pin: string, kind: StampKind): Promise<KioskStampResult> {
  const clientUuid = newUuid()
  const stampedAt = serverAdjustedNow()
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    saveQueue([...loadQueue(), { clientUuid, userId, kind, stampedAt }])
    throw new KioskApiError(0, 'offline')
  }
  try {
    // stamped_at wird im Online-Pfad bewusst NICHT mitgeschickt: fehlt es, nimmt der Server seine eigene
    // Zeit und klassifiziert als 'live'. Damit kann eine falsch gestellte Tablet-Uhr keinen Live-Stempel in
    // einen Nachzügler verwandeln. Nur Warteschlangen-Items brauchen einen Zeitstempel — der ist dann
    // serverkorrigiert (serverAdjustedNow).
    const res = await kioskRequest<KioskStampResult>('kiosk/stamp', {
      method: 'POST',
      body: JSON.stringify({ userId, pin, kind, client_uuid: clientUuid }),
    })
    noteKioskServerNow(res.serverNow)
    return res
  } catch (err) {
    // Netzfehler (kein HTTP-Status) → in die Warteschlange. Fachliche Fehler (falsche PIN, Status) NICHT:
    // die gehören sofort und sichtbar an den Mitarbeiter, der davorsteht.
    if (err instanceof KioskApiError && err.status !== 0) throw err
    saveQueue([...loadQueue(), { clientUuid, userId, kind, stampedAt }])
    throw new KioskApiError(0, 'offline')
  }
}

/** Re-Entrancy-Guard (Muster: offline/outbox.ts). Mount-Flush, online-Event und Intervall können sonst
 *  gleichzeitig laufen und dasselbe Item doppelt senden. */
let flushing = false

/**
 * Reicht die Warteschlange nach. FIFO und einzeln.
 *
 * Verwerfen ist hier die gefährlichste Operation — hinter jedem Eintrag steht erfasste Arbeitszeit, und der
 * Mitarbeiter hat am Terminal die Bestätigung „wird nachgereicht" gesehen. Deshalb:
 * - 429 / 5xx / Netzfehler → abbrechen, Reihenfolge erhalten, später erneut. (429 ist transient: der Server
 *   drosselt pro Zielkonto, ein früherer PIN-Tippfehler darf keinen Stempel kosten.)
 * - 401 → das Gerät ist abgemeldet oder widerrufen. Abbrechen, NICHT verwerfen: nach erneuter Kopplung
 *   lässt sich alles nachreichen.
 * - echte fachliche Ablehnung (400/403/404/409/422) → in den sichtbaren „nicht zustellbar"-Bereich, nie
 *   stillschweigend löschen.
 */
export async function flushKioskQueue(): Promise<number> {
  if (!getKioskToken() || flushing) return 0
  flushing = true
  try {
    let sent = 0
    for (const item of loadQueue()) {
      try {
        await kioskRequest('kiosk/stamp', {
          method: 'POST',
          body: JSON.stringify({
            userId: item.userId,
            kind: item.kind,
            client_uuid: item.clientUuid,
            stamped_at: item.stampedAt,
            unverified: true,
          }),
        })
        saveQueue(loadQueue().filter((q) => q.clientUuid !== item.clientUuid))
        sent++
      } catch (err) {
        const status = err instanceof KioskApiError ? err.status : 0
        if (status === 401 || status === 429 || status >= 500 || status === 0) break
        deadLetter(item)
        saveQueue(loadQueue().filter((q) => q.clientUuid !== item.clientUuid))
      }
    }
    return sent
  } finally {
    flushing = false
  }
}
